import { Page, useCurrent, Grid } from "./common";
import { useNavigate } from "react-router-dom";
import { PageHead, Plate, StatusBadge } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { DeckBuilder } from "./ConceptDeck";
import { fmtDate } from "../lib/format";

/* "Design presentation" is where an architect looks for the thing they present.
   Historically that was a per-space approval list, which had no way to add a
   space — so an empty one was a dead end. It now hosts the presentation builder
   (concept, design proposal or renders) and keeps the space list underneath
   whenever there actually are spaces to approve. */
export default function Presentation() {
  const p = useCurrent();
  const nav = useNavigate();
  const me = useSession();
  const canEdit = useCan("presentation");
  if (!p) return null;
  const pr = p.presentation;
  const spaces = pr.spaces ?? [];
  const deck = p.deck;

  /* No spaces to approve — this section is the builder, nothing else. */
  if (!spaces.length) {
    return (
      <Page>
        <PageHead kicker={`${p.name} · ${deck?.stage ?? "Design"}`} title="Design presentation">
          {deck?.status === "issued" && (
            <button className="btn ghost sm" onClick={() => nav(`/portal/${p.id}/concept`)}>
              <Icon.arrowUpRight width={15} />Client view
            </button>
          )}
        </PageHead>
        {canEdit
          ? <DeckBuilder p={p} by={me?.name ?? "Studio"} />
          : <p className="muted">Only the design team can build the presentation.</p>}
      </Page>
    );
  }
  const approved = pr.spaces.filter((s) => s.response === "approved").length;
  const revise = pr.spaces.filter((s) => s.response === "revise").length;
  const pending = pr.spaces.filter((s) => s.response === "pending").length;

  return (
    <Page>
      <PageHead kicker={`${p.name} · Developed Design`} title="Design presentation">
        <StatusBadge status={pr.status} />
        {canEdit && <button className="btn ghost sm" onClick={() => nav(`/studio/${p.id}/concept`)}><Icon.present width={15} />Presentation builder</button>}
        {canEdit && pr.status === "draft" && <button className="btn accent sm" onClick={() => actions.issuePresentation(p.id)}><Icon.send width={15} />Issue to client</button>}
        <button className="btn ghost sm" onClick={() => nav(`/portal/${p.id}/presentation`)}><Icon.arrowUpRight width={15} />Client view</button>
      </PageHead>

      {/* approval summary */}
      <div className="card pad reveal" style={{ marginBottom: 24 }}>
        <div className="row between wrap" style={{ gap: 16 }}>
          <div>
            <div className="serif" style={{ fontSize: 20 }}>{pr.title}</div>
            <div className="dim" style={{ fontSize: 13, marginTop: 3 }}>{pr.issuedOn ? `Issued ${fmtDate(pr.issuedOn)}` : "Not yet issued"} · {pr.spaces.length} spaces</div>
          </div>
          <div className="row" style={{ gap: 22 }}>
            <Tally n={approved} label="Approved" color="var(--ok)" />
            <Tally n={revise} label="To revise" color="var(--warn)" />
            <Tally n={pending} label="Awaiting" color="var(--ink-3)" />
          </div>
        </div>
        <div style={{ display: "flex", height: 8, borderRadius: 8, overflow: "hidden", marginTop: 18, background: "var(--line)" }}>
          {approved > 0 && <div style={{ flex: approved, background: "var(--ok)" }} />}
          {revise > 0 && <div style={{ flex: revise, background: "var(--warn)" }} />}
          {pending > 0 && <div style={{ flex: pending, background: "var(--line-2)" }} />}
        </div>
      </div>

      <Grid cols="repeat(auto-fill, minmax(360px, 1fr))" gap={22}>
        {pr.spaces.map((s, i) => (
          <div key={s.id} className="card hoverable reveal" style={{ overflow: "hidden" }}>
            <div style={{ position: "relative" }}>
              <Plate hue={s.hue} h={190} seed={i + 1} label={s.option} />
              <div style={{ position: "absolute", top: 12, right: 12 }}><StatusBadge status={s.response} /></div>
            </div>
            <div style={{ padding: 20 }}>
              <h3 className="serif" style={{ fontSize: 22 }}>{s.name}</h3>
              <p className="muted" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.55 }}>{s.description}</p>
              <div className="row wrap" style={{ gap: 6, marginTop: 14 }}>
                {s.materials.map((m) => <span key={m} className="badge">{m}</span>)}
              </div>
              {s.comment && (
                <div className="panel" style={{ padding: "12px 14px", marginTop: 14 }}>
                  <div className="row" style={{ gap: 8 }}><Icon.chat width={15} style={{ color: "var(--accent-ink)", flexShrink: 0, marginTop: 2 }} />
                    <div><div style={{ fontSize: 14, fontStyle: "italic" }}>“{s.comment}”</div><div className="dim" style={{ fontSize: 11, marginTop: 4 }}>{p.client.name} · {fmtDate(s.respondedOn)}</div></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </Grid>
    </Page>
  );
}

function Tally({ n, label, color }: { n: number; label: string; color: string }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div className="serif" style={{ fontSize: 28, color }}>{n}</div>
      <div className="kicker" style={{ fontSize: 9.5 }}>{label}</div>
    </div>
  );
}
